import { OG_IMAGES } from "@/lib/og-images";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Sparkles, ArrowUpRight } from "lucide-react";
import { PageBreadcrumb } from "@/components/site/PageBreadcrumb";
import { RelatedKeywords } from "@/components/site/RelatedKeywords";
import { RelatedPages } from "@/components/site/RelatedPages";

const TITLE = "Aion — AI Course & Prompt Engineering Class";
const DESC =
  "Aion is a cinematic AI course teaching how to use AI for real work — prompt engineering, AI agents and the mental models most people never learn. Join for ₹11.";
const URL = "https://rajshamani.life/aion";
const AION_URL = "https://aion.engineer";
const OG = OG_IMAGES.ventures;

const faq = [
  {
    q: "What is Aion?",
    a: "Aion is a cinematic online AI course built by Aion Studios. It teaches how to actually use AI for real work — prompt engineering, AI agents and the mental models behind them — rather than listing tools.",
  },
  {
    q: "How much does the Aion AI course cost?",
    a: "Entry to the course is ₹11. You can join directly at aion.engineer.",
  },
  {
    q: "Who is the Aion course for?",
    a: "Founders, operators, marketers, students and creators who want to use AI as leverage in daily work. No coding background is required.",
  },
  {
    q: "What does the Aion course cover?",
    a: "Prompt engineering fundamentals, building and directing AI agents, workflow design, and the thinking frameworks that separate people who get real output from AI from people who only get demos.",
  },
  {
    q: "Where can I join Aion?",
    a: "Enrolment happens on the official Aion website at https://aion.engineer.",
  },
];

export const Route = createFileRoute("/aion")({
  head: () => ({
    meta: [
      { title: TITLE, key: "title" },
      { name: "description", content: DESC, key: "description" },
      { property: "og:title", content: TITLE, key: "og:title" },
      { property: "og:description", content: DESC, key: "og:description" },
      { property: "og:url", content: URL, key: "og:url" },
      { property: "og:type", content: "article", key: "og:type" },
      { property: "og:image", content: OG, key: "og:image" },
      { property: "og:image:alt", content: "Aion AI course", key: "og:image:alt" },
      { name: "twitter:card", content: "summary_large_image", key: "twitter:card" },
      { name: "twitter:title", content: TITLE, key: "twitter:title" },
      { name: "twitter:description", content: DESC, key: "twitter:description" },
      { name: "twitter:image", content: OG, key: "twitter:image" },
      { property: "og:locale", content: "en_IN", key: "og:locale" },
      { name: "twitter:site", content: "@rajshamani", key: "twitter:site" },
      { name: "twitter:creator", content: "@rajshamani", key: "twitter:creator" },
      { name: "geo.region", content: "IN", key: "geo.region" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          "@id": `${URL}#webpage`,
          name: TITLE,
          headline: TITLE,
          description: DESC,
          url: URL,
          inLanguage: "en",
          isPartOf: { "@type": "WebSite", "@id": "https://rajshamani.life/#website" },
          breadcrumb: { "@id": `${URL}#breadcrumb` },
          publisher: { "@type": "Organization", "@id": "https://rajshamani.life/#organization" },
          primaryImageOfPage: { "@type": "ImageObject", url: OG },
          image: OG,
          about: {
            "@type": "Course",
            "@id": `${AION_URL}#course`,
            name: "Aion — AI Course & Prompt Engineering Class",
            description: DESC,
            url: AION_URL,
            inLanguage: "en",
            provider: {
              "@type": "Organization",
              name: "Aion Studios",
              url: AION_URL,
            },
            hasCourseInstance: {
              "@type": "CourseInstance",
              courseMode: "online",
              courseWorkload: "PT6H",
            },
            offers: {
              "@type": "Offer",
              price: "11",
              priceCurrency: "INR",
              url: AION_URL,
              availability: "https://schema.org/InStock",
            },
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "@id": `${URL}#faq`,
          mainEntity: faq.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: AionPage,
});

function AionPage() {
  return (
    <div>
      <PageBreadcrumb current="Aion" />

      <section className="bg-noir border-b border-border/50">
        <div className="mx-auto max-w-5xl px-6 py-24">
          <div className="text-xs tracking-widest uppercase text-gold mb-4 inline-flex items-center gap-2">
            <Sparkles size={14} /> AI course
          </div>
          <h1 className="text-4xl md:text-6xl font-bold leading-tight">
            <span className="text-gradient-gold">Aion</span> — the AI course for people who want
            real output
          </h1>
          <p className="mt-8 text-lg text-muted-foreground max-w-3xl leading-relaxed">
            Aion is a cinematic AI course teaching how to use AI for real work: prompt engineering,
            AI agents, and the mental models most people never learn. Entry is ₹11.
          </p>
          <a
            href={AION_URL}
            target="_blank"
            rel="noopener"
            className="mt-10 inline-flex items-center gap-2 rounded-md bg-gold px-6 py-3 font-medium text-primary-foreground hover:opacity-90 transition"
          >
            Visit aion.engineer <ArrowUpRight size={16} />
          </a>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-6 py-20">
        <h2 className="text-3xl md:text-4xl font-bold mb-6">What Aion teaches</h2>
        <div className="space-y-5 text-muted-foreground leading-relaxed max-w-3xl">
          <p>
            Most AI content is a tour of tools. Aion is the opposite: it starts with how to think
            about a model, then builds up to prompts, agents and workflows you can actually run
            inside a business. The goal is leverage, not novelty.
          </p>
          <p>
            The course is built for Indian founders, operators, marketers and students who keep
            hearing that AI will change their work but have never been shown a repeatable way to use
            it. No coding background is assumed.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-6 pb-20">
        <h2 className="text-3xl md:text-4xl font-bold mb-8">Inside the course</h2>
        <div className="grid gap-4 md:grid-cols-3">
          {[
            [
              "Prompt engineering",
              "How to brief a model the way you would brief a sharp new hire — context, constraints, examples and iteration.",
            ],
            [
              "AI agents",
              "Chaining steps, giving tools and memory, and knowing when an agent is the right answer instead of a single prompt.",
            ],
            [
              "Mental models",
              "The reasoning frameworks that decide whether AI saves you ten minutes or ten hours a week.",
            ],
          ].map(([title, body]) => (
            <div key={title} className="rounded-xl border border-border bg-card p-6">
              <h3 className="font-bold mb-2">{title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-6 pb-20">
        <h2 className="text-3xl md:text-4xl font-bold mb-6">Why it sits on this site</h2>
        <div className="space-y-5 text-muted-foreground leading-relaxed max-w-3xl">
          <p>
            The same argument runs through the{" "}
            <Link to="/raj-shamani-podcast" className="text-gold underline underline-offset-4">
              Figuring Out podcast
            </Link>{" "}
            and{" "}
            <Link to="/build-dont-talk" className="text-gold underline underline-offset-4">
              Build, Don&apos;t Talk
            </Link>
            : skills compound faster than opinions. Aion is that idea applied to the single biggest
            skill shift of this decade.
          </p>
          <p>
            If you are building a brand or a business alongside it, the{" "}
            <Link to="/guides/d2c-brands-india" className="text-gold underline underline-offset-4">
              D2C brands in India guide
            </Link>{" "}
            and the{" "}
            <Link to="/ventures" className="text-gold underline underline-offset-4">
              ventures page
            </Link>{" "}
            cover the commercial side.
          </p>
        </div>
        <a
          href={AION_URL}
          target="_blank"
          rel="noopener"
          className="mt-8 inline-flex items-center gap-2 rounded-md border border-gold/50 px-6 py-3 font-medium text-gold hover:bg-gold/10 transition"
        >
          Join Aion for ₹11 <ArrowUpRight size={16} />
        </a>
      </section>

      <section className="mx-auto max-w-5xl px-6 pb-20">
        <h2 className="text-3xl md:text-4xl font-bold mb-8">Frequently asked questions</h2>
        <div className="space-y-6">
          {faq.map((f) => (
            <div key={f.q} className="rounded-xl border border-border bg-card p-6">
              <h3 className="font-bold mb-2">{f.q}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <RelatedKeywords currentPath="/aion" />

      <RelatedPages pages={["ventures", "about", "podcast", "contact", "faq"]} />
    </div>
  );
}
